type McpContentPart = {
  readonly type?: unknown;
  readonly text?: unknown;
};

type McpToolResult = {
  readonly content?: ReadonlyArray<McpContentPart>;
  readonly structuredContent?: unknown;
  readonly isError?: boolean;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Returns true when tool output is an MCP CallToolResult with `isError` set.
 * MCP tools often keep AI SDK state as `output-available` while flagging errors this way.
 * @param output Tool-call output from an MCP-backed AI SDK tool.
 */
export function isMcpToolErrorOutput(output: unknown): boolean {
  return isRecord(output) && (output as McpToolResult).isError === true;
}

/**
 * Derives display text from structured MCP tool output when content[] is empty.
 * @param structured Parsed structuredContent object.
 * @returns Plain text for UI labels, or undefined when no text representation applies.
 */
function deriveTextFromStructured(structured: Record<string, unknown>): string | undefined {
  if (typeof structured.stdout === 'string') {
    let text = structured.stdout;
    if (typeof structured.stderr === 'string' && structured.stderr.length > 0) {
      text += `\n--- stderr ---\n${structured.stderr}`;
    }
    if (typeof structured.exitCode === 'number' && typeof structured.elapsedMs === 'number') {
      text += `\n[exit:${structured.exitCode} | ${structured.elapsedMs}ms]`;
    }
    return text;
  }

  if (typeof structured.content === 'string' && structured.content.length > 0) {
    return structured.content;
  }

  if (structured.found === false && typeof structured.message === 'string') {
    return structured.message;
  }

  if (
    structured.found === true &&
    typeof structured.summary === 'string' &&
    typeof structured.path === 'string'
  ) {
    const title = typeof structured.title === 'string' ? structured.title : '';
    return `File: ${structured.path}\nTitle: ${title}\nSummary: ${structured.summary}`;
  }

  return undefined;
}

/**
 * Returns structuredContent when present on a CallToolResult-shaped tool output.
 * @param output Tool-call output from an MCP-backed AI SDK tool.
 * @returns Parsed structured payload, or undefined.
 */
export function getMcpToolStructuredOutput(output: unknown): unknown | undefined {
  if (typeof output === 'object' && output !== null && 'structuredContent' in output) {
    const structured = (output as McpToolResult).structuredContent;
    return structured !== undefined ? structured : undefined;
  }
  return undefined;
}

/**
 * Extracts the first text part from an MCP CallToolResult-shaped tool output.
 * Falls back to structured fields (stdout, content, message) when content[] is empty.
 * @param output Tool-call output from an MCP-backed AI SDK tool.
 * @returns Plain text content, if present.
 */
export function getMcpToolTextOutput(output: unknown): string | undefined {
  if (typeof output === 'string') {
    const trimmed = output.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (typeof output !== 'object' || output === null) {
    return undefined;
  }

  const result = output as McpToolResult;
  const content = result.content;
  if (Array.isArray(content)) {
    for (const part of content) {
      if (
        typeof part === 'object' &&
        part !== null &&
        part.type === 'text' &&
        typeof part.text === 'string'
      ) {
        const trimmed = part.text.trim();
        if (trimmed.length > 0) {
          return trimmed;
        }
      }
    }
  }

  const structured = getMcpToolStructuredOutput(output);
  if (isRecord(structured)) {
    return deriveTextFromStructured(structured);
  }

  return undefined;
}

/**
 * Parses JSON from MCP tool output, preferring structuredContent over text content.
 * @param output Tool-call output from an MCP-backed AI SDK tool.
 * @returns Parsed value, or undefined when output is missing or invalid.
 */
export function parseMcpToolJsonOutput(output: unknown): unknown | undefined {
  const structured = getMcpToolStructuredOutput(output);
  if (structured !== undefined) {
    return structured;
  }

  const text = getMcpToolTextOutput(output);
  if (text === undefined) {
    return undefined;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

/**
 * Returns true when forge-discovery emitted a plain-text empty or not-found message.
 * Also checks structured `{ found: false }` and `{ count: 0 }` shapes.
 * @param text MCP text output (legacy persisted tool calls).
 * @param structured Optional structured output object.
 * @returns Whether the message indicates no data.
 */
export function isForgeDiscoveryEmptyMessage(text: string, structured?: unknown): boolean {
  if (isRecord(structured)) {
    if (structured.found === false) {
      return true;
    }
    if (structured.count === 0) {
      return true;
    }
  }

  const normalized = text.trim();
  if (normalized.length === 0) {
    return true;
  }
  if (normalized.startsWith('No ')) {
    return true;
  }
  if (/^Skill "[^"]+" not found/.test(normalized)) {
    return true;
  }
  if (/^Project "[^"]+" not found/.test(normalized)) {
    return true;
  }
  return false;
}

/**
 * Returns true when parsed MCP output indicates an empty forge-discovery result.
 * @param output Tool-call output from forge-discovery MCP tools.
 * @returns Whether the result represents no data.
 */
export function isForgeDiscoveryEmptyOutput(output: unknown): boolean {
  const parsed = parseMcpToolJsonOutput(output);
  if (isRecord(parsed)) {
    if (parsed.found === false) {
      return true;
    }
    if (parsed.count === 0) {
      return true;
    }
  }

  const text = getMcpToolTextOutput(output);
  if (text === undefined) {
    return false;
  }
  return isForgeDiscoveryEmptyMessage(text, parsed);
}
